import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

/**
 * Article bodies, code-split by slug.
 *
 * Kept apart from registry.ts so listing surfaces (homepage, hub, sitemap) can
 * read guide metadata without pulling every article's JSX into the initial
 * bundle.
 *
 * HYDRATION: a prerendered guide page cannot hydrate through a Suspense
 * boundary that is still waiting on a dynamic import — React bails out with
 * error #421 ("received an update before it finished hydrating") and re-renders
 * the whole article on the client, discarding the HTML we just prerendered.
 *
 * So the body is *preloaded* before hydration (see main.tsx) and, once
 * resolved, rendered directly instead of through Suspense. The lazy wrapper
 * remains for client-side navigation, where suspending is fine.
 */

type BodyModule = { default: ComponentType };
type Loader = () => Promise<BodyModule>;

const LOADERS: Record<string, Loader> = {
  'sending-money-to-korea': () => import('./sending-money-to-korea'),
  'f4-visa-guide': () => import('./f4-visa-guide'),
  'finding-bilingual-mentors': () => import('./finding-bilingual-mentors'),
};

/** Slug → resolved component, populated by preload or by the lazy factory. */
const resolved = new Map<string, ComponentType>();

export const GUIDE_BODIES: Record<string, LazyExoticComponent<ComponentType>> = Object.fromEntries(
  Object.entries(LOADERS).map(([slug, load]) => [
    slug,
    lazy(async () => {
      const mod = await load();
      resolved.set(slug, mod.default);
      return mod;
    }),
  ])
);

export function getGuideBody(slug: string): LazyExoticComponent<ComponentType> | undefined {
  return GUIDE_BODIES[slug];
}

/** Already-resolved body, renderable synchronously with no Suspense boundary. */
export function getLoadedGuideBody(slug: string): ComponentType | undefined {
  return resolved.get(slug);
}

export function hasGuideBody(slug: string): boolean {
  return slug in LOADERS;
}

/**
 * Resolve a guide body ahead of render. Called before hydration on the client
 * and before renderToPipeableStream on the server so both produce identical,
 * Suspense-free markup.
 */
export async function preloadGuideBody(slug: string): Promise<ComponentType | undefined> {
  const load = LOADERS[slug];
  if (!load) return undefined;
  const mod = await load();
  resolved.set(slug, mod.default);
  return mod.default;
}

/** Extracts a guide slug from a pathname, or null if the path isn't a guide. */
export function guideSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/guides\/([^/]+)\/?$/);
  return match ? match[1] : null;
}
